import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { 
  Download, 
  FileText, 
  Table2, 
  Package, 
  FolderArchive,
  FolderOpen,
  Check,
  Loader2,
  Edit2,
  MapPin,
  HardDrive
} from 'lucide-react';

const DownloadDialog = ({ 
  open, 
  onOpenChange, 
  fileType, 
  defaultFilename, 
  fileSize,
  onDownload,
  downloading = false
}) => {
  const [filename, setFilename] = useState(defaultFilename || 'download');
  const [downloaded, setDownloaded] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [savedPath, setSavedPath] = useState('');
  const [saveMethod, setSaveMethod] = useState(''); // 'picker' or 'downloads'

  // Reset state when dialog opens with new filename
  useEffect(() => {
    if (open && defaultFilename) {
      setFilename(defaultFilename);
      setDownloaded(false);
      setSavedPath('');
      setSaveMethod('');
    }
  }, [open, defaultFilename]);

  const getFileIcon = () => {
    switch (fileType) {
      case 'pdf':
      case 'pdf_report':
        return <FileText className="w-12 h-12 text-red-500" />;
      case 'csv':
      case 'csv_export':
        return <Table2 className="w-12 h-12 text-green-500" />;
      case 'job_packet':
        return <Package className="w-12 h-12 text-purple-500" />;
      case 'zip':
      case 'photos_zip':
        return <FolderArchive className="w-12 h-12 text-blue-500" />;
      default:
        return <Download className="w-12 h-12 text-slate-500" />;
    }
  };

  const getFileTypeLabel = () => {
    switch (fileType) {
      case 'pdf':
      case 'pdf_report':
        return 'PDF Report';
      case 'csv':
      case 'csv_export':
        return 'CSV Export';
      case 'job_packet':
        return 'Job Packet';
      case 'zip':
      case 'photos_zip':
        return 'Photos Archive';
      default:
        return 'File';
    }
  };

  const getMimeType = () => {
    switch (fileType) {
      case 'pdf':
      case 'pdf_report':
        return 'application/pdf';
      case 'csv':
      case 'csv_export':
        return 'text/csv';
      case 'job_packet':
        return 'application/json';
      case 'zip':
      case 'photos_zip':
        return 'application/zip';
      default:
        return 'application/octet-stream';
    }
  };

  const getExtension = () => {
    switch (fileType) {
      case 'pdf':
      case 'pdf_report':
        return '.pdf';
      case 'csv':
      case 'csv_export':
        return '.csv';
      case 'job_packet':
        return '.json';
      case 'zip':
      case 'photos_zip':
        return '.zip';
      default:
        return '';
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Calculating...';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Use File System Access API for "Save As" dialog
  const handleSaveAs = async () => {
    setIsDownloading(true);
    setSavedPath('');
    setSaveMethod('');
    
    try {
      // Get the file data from the parent component
      const fileData = await onDownload(filename, true); // true = return blob instead of downloading
      
      if (!fileData) {
        throw new Error('No file data received');
      }

      const ext = getExtension();
      const fullFilename = filename + ext;

      // Check if File System Access API is supported
      if ('showSaveFilePicker' in window) {
        try {
          const fileHandle = await window.showSaveFilePicker({
            suggestedName: fullFilename,
            types: [{
              description: getFileTypeLabel(),
              accept: { [getMimeType()]: [ext] }
            }]
          });
          
          const writable = await fileHandle.createWritable();
          await writable.write(fileData);
          await writable.close();
          
          // Get the file name from the handle (this is what the user chose)
          setSavedPath(fileHandle.name);
          setSaveMethod('picker');
          setDownloaded(true);
          return;
        } catch (err) {
          // User cancelled or API failed, fall back to regular download
          if (err.name === 'AbortError') {
            setIsDownloading(false);
            return; // User cancelled
          }
          console.log('File System API failed, falling back to regular download');
        }
      }
      
      // Fallback: Regular download (for browsers that don't support File System Access API)
      const url = window.URL.createObjectURL(fileData);
      const link = document.createElement('a');
      link.href = url;
      link.download = fullFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setSavedPath(fullFilename);
      setSaveMethod('downloads');
      setDownloaded(true);
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-slate-900 border-slate-700 text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <Download className="w-5 h-5 text-blue-400" />
            Save File
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* File Preview */}
          <div className="flex items-center gap-4 p-4 bg-slate-800 rounded-lg border border-slate-700">
            {getFileIcon()}
            <div className="flex-1 min-w-0">
              <p className="font-medium text-white truncate">
                {filename}{getExtension()}
              </p>
              <p className="text-sm text-slate-400">
                {getFileTypeLabel()} • {formatFileSize(fileSize)}
              </p>
            </div>
          </div>

          {/* Show save location after download */}
          {downloaded && savedPath && (
            <div className="p-4 bg-green-900/30 border border-green-700 rounded-lg">
              <div className="flex items-center gap-2 text-green-400 mb-2">
                <Check className="w-5 h-5" />
                <span className="font-medium">File Saved Successfully!</span>
              </div>
              <div className="flex items-start gap-2 text-sm">
                <HardDrive className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-slate-300 font-medium">
                    {saveMethod === 'picker' ? 'Saved as:' : 'Downloaded to:'}
                  </p>
                  <p className="text-blue-400 break-all font-mono text-xs mt-1 bg-slate-800 px-2 py-1 rounded">
                    {saveMethod === 'picker' ? (
                      <>📁 {savedPath}</>
                    ) : (
                      <>📥 Downloads / {savedPath}</>
                    )}
                  </p>
                  {saveMethod === 'picker' && (
                    <p className="text-slate-500 text-xs mt-2">
                      File saved to your chosen location
                    </p>
                  )}
                  {saveMethod === 'downloads' && (
                    <p className="text-slate-500 text-xs mt-2">
                      Check your browser&apos;s Downloads folder
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Filename Editor - hide after download */}
          {!downloaded && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-slate-300">
                <Edit2 className="w-4 h-4" />
                Filename
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  value={filename}
                  onChange={(e) => setFilename(e.target.value)}
                  placeholder="Enter filename"
                  className="flex-1 bg-slate-800 border-slate-600 text-white placeholder:text-slate-500 focus:border-blue-500"
                />
                <span className="text-slate-400 text-sm font-mono">{getExtension()}</span>
              </div>
            </div>
          )}

          {/* Save As Button or Close Button */}
          {downloaded ? (
            <Button 
              onClick={() => onOpenChange(false)}
              className="w-full bg-slate-700 hover:bg-slate-600"
            >
              Close
            </Button>
          ) : (
            <Button 
              onClick={handleSaveAs}
              disabled={isDownloading || !filename.trim()}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              {isDownloading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Preparing...
                </>
              ) : (
                <>
                  <FolderOpen className="w-4 h-4 mr-2" />
                  Choose Location & Save
                </>
              )}
            </Button>
          )}
          
          {!downloaded && (
            <p className="text-xs text-slate-500 text-center">
              A file browser will open to let you choose where to save the file
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DownloadDialog;
